
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Paintbrush, RefreshCw, Check, Sun, Moon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import ColorPicker from "./ColorPicker";
import ColorPreview from "./ColorPreview";
import { useTheme } from "@/hooks/use-theme";
import ColorPresets from "./ColorPresets";
import { ColorPreset } from "@/contexts/theme/ThemeContext";
import { useState } from "react";
import { toast } from "sonner";

const ColorSettings = () => {
  const { theme, setTheme, colors } = useTheme();
  const [primaryColor, setPrimaryColor] = useState(theme === 'dark' ? colors.dark.primary : colors.light.primary);
  const [accentColor, setAccentColor] = useState(theme === 'dark' ? colors.dark.accent : colors.light.accent);
  const [isSaving, setIsSaving] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(theme === 'dark');
  
  const isMobile = useIsMobile();

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    setIsDarkMode(newTheme === "dark");
    
    // Update colors based on new theme
    setPrimaryColor(newTheme === 'dark' ? colors.dark.primary : colors.light.primary);
    setAccentColor(newTheme === 'dark' ? colors.dark.accent : colors.light.accent);
  };
  
  const handleSaveColors = () => {
    setIsSaving(true);
    
    try {
      const root = document.documentElement;
      root.style.setProperty('--primary', primaryColor);
      root.style.setProperty('--accent', accentColor);
      
      // Save colors to localStorage for persistence
      localStorage.setItem('theme-primary', primaryColor);
      localStorage.setItem('theme-accent', accentColor);
      
      // Update any CSS custom properties that depend on primary/accent
      if (root.classList.contains('dark')) {
        // Ensure dark mode colors are visible
        const darkerPrimary = primaryColor;
        root.style.setProperty('--primary', darkerPrimary);
      }
      
      toast.success('Theme colors updated successfully');
    } catch (error) {
      toast.error('Failed to update theme colors');
      console.error('Error updating theme:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetColors = () => {
    // Default colors (light/dark aware)
    const isDark = document.documentElement.classList.contains('dark');
    const defaultPrimary = isDark ? '#3D5A74' : '#2C3E50';
    const defaultAccent = isDark ? '#4A6B8A' : '#3D5A74';
    
    setPrimaryColor(defaultPrimary);
    setAccentColor(defaultAccent);
    
    const root = document.documentElement;
    root.style.setProperty('--primary', defaultPrimary);
    root.style.setProperty('--accent', defaultAccent);
    
    localStorage.removeItem('theme-primary');
    localStorage.removeItem('theme-accent');
    
    toast('Colors reset to default');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Paintbrush size={20} />
          Legacy Color Settings
        </CardTitle>
        <CardDescription>
          Basic customization for primary and accent colors (consider using the
          advanced settings above)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className={`grid grid-cols-1 gap-6 ${
            !isMobile ? "md:grid-cols-2" : ""
          }`}
        >
          <div className="space-y-4">
            <ColorPicker
              label="Primary Color"
              color={primaryColor}
              onChange={setPrimaryColor}
            />
            <ColorPicker
              label="Accent Color"
              color={accentColor}
              onChange={setAccentColor}
            />
            <Button variant="outline" onClick={toggleTheme} className="w-full">
              {isDarkMode ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Moon className="mr-2 h-4 w-4" />
              )}
              {isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            </Button>
          </div>

          <div className="space-y-4">
            <ColorPresets
              onSelectPreset={(preset: ColorPreset) => {
                setPrimaryColor(preset.primary);
                setAccentColor(preset.accent);
              }}
            />
            <ColorPreview
              primaryColor={primaryColor}
              accentColor={accentColor}
            />
          </div>
        </div>

        <div
          className={`flex ${
            isMobile ? "flex-col space-y-2" : "justify-end space-x-2"
          } mt-6`}
        >
          <Button
            variant="outline"
            onClick={handleResetColors}
            className={isMobile ? "w-full" : ""}
          >
            <RefreshCw size={16} className="mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSaveColors}
            disabled={isSaving}
            className={isMobile ? "w-full" : ""}
          >
            {isSaving ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                Saving...
              </>
            ) : (
              <>
                <Check size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ColorSettings;
